import React from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import metadata from "../../data/metadata.json";

/**
 * Seo — centralised page metadata.
 *
 * Rendered once in App.js (inside the Router). It reads the current route via
 * useLocation, looks up the matching entry in src/data/metadata.json (exact
 * match first, then dynamic ":param" patterns), merges it over the global
 * defaults, and emits title / description / keywords / canonical / Open Graph /
 * Twitter tags plus an Organization JSON-LD block for the college.
 *
 * Optional props (title, description, image) let a page override the values if
 * ever needed, without changing the default location-driven behaviour.
 */

const { global, pages } = metadata;

function findPageMeta(pathname) {
  // normalise trailing slash (keep root "/")
  const p = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  if (pages[p]) return pages[p];
  // dynamic patterns e.g. "/blog/:id", "/department/:id/:tab"
  for (const key of Object.keys(pages)) {
    if (!key.includes(":")) continue;
    const rx = new RegExp("^" + key.replace(/:[^/]+/g, "[^/]+") + "$");
    if (rx.test(p)) return pages[key];
  }
  return null;
}

function absUrl(pathOrUrl) {
  if (!pathOrUrl) return global.siteUrl;
  return /^https?:\/\//.test(pathOrUrl)
    ? pathOrUrl
    : global.siteUrl + (pathOrUrl.startsWith("/") ? "" : "/") + pathOrUrl;
}

const Seo = ({ title, description, image }) => {
  const { pathname } = useLocation();
  const pageMeta = findPageMeta(pathname) || pages["*"] || {};

  // Titles in metadata.json are complete, final titles (they already include
  // the "| NCET ..." suffix), so they are used verbatim — no title template.
  const fullTitle = title || pageMeta.title || global.defaultTitle;

  const desc = description || pageMeta.description || global.description;
  const keywords = pageMeta.keywords || global.keywords;
  const canonical = global.siteUrl + (pathname === "/" ? "" : pathname);
  const ogImage = absUrl(image || pageMeta.ogImage || global.ogImage);
  const isAdminRoute = pathname.startsWith("/admin");
  const robots = pageMeta.robots || (isAdminRoute ? "noindex, nofollow, noarchive" : "index, follow");

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <meta name="robots" content={robots} />
      {isAdminRoute && <meta name="googlebot" content="noindex, nofollow, noarchive" />}
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={global.siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={global.locale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      {global.twitterHandle ? (
        <meta name="twitter:site" content={global.twitterHandle} />
      ) : null}
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {/* Organization structured data */}
      <script type="application/ld+json">
        {JSON.stringify(global.organization)}
      </script>
    </Helmet>
  );
};

export default Seo;
